import { useEffect, useMemo, useState } from 'react';
import {
  CHAT_THREAD_DEFAULT_GRADIENT,
  getChatThreadBackgroundCss,
  getChatThreadPresetStorageValue,
  resolveChatThreadImageUrl,
} from '../utils/chatThreadBackgrounds';
import { getUserThreadBackgroundDataUrl } from '../utils/chatThreadUserBackgroundStorage';

/**
 * Стили фона чата: градиент-пресет → своё фото (локально) → фото админа → градиент по умолчанию.
 * @param {'mobile' | 'desktop'} userBgSurface — отдельное своё фото для мобильного и десктопного чата.
 */
export function useChatThreadBackgroundStyle(userBgSurface = 'desktop') {
  const [tick, setTick] = useState(0);
  const [adminImagePath, setAdminImagePath] = useState('');
  const [userCustomDataUrl, setUserCustomDataUrl] = useState('');

  useEffect(() => {
    const bump = () => setTick((x) => x + 1);
    const onPreset = () => bump();
    const onBgUpd = () => bump();
    const onUserBg = () => bump();
    window.addEventListener('chat-thread-user-preset-changed', onPreset);
    window.addEventListener('chat-thread-bg-updated', onBgUpd);
    window.addEventListener('chat-thread-user-custom-bg-changed', onUserBg);
    window.addEventListener('storage', onPreset);
    return () => {
      window.removeEventListener('chat-thread-user-preset-changed', onPreset);
      window.removeEventListener('chat-thread-bg-updated', onBgUpd);
      window.removeEventListener('chat-thread-user-custom-bg-changed', onUserBg);
      window.removeEventListener('storage', onPreset);
    };
  }, []);

  useEffect(() => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      setAdminImagePath('');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch('/api/chat/thread-background', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        const u = d?.imageUrl != null ? String(d.imageUrl).trim() : '';
        setAdminImagePath(u);
      } catch {
        if (!cancelled) setAdminImagePath('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await getUserThreadBackgroundDataUrl(userBgSurface);
      if (!cancelled) setUserCustomDataUrl(u || '');
    })();
    return () => {
      cancelled = true;
    };
  }, [tick, userBgSurface]);

  return useMemo(() => {
    void tick;
    const presetId = getChatThreadPresetStorageValue();
    const presetCss = presetId ? getChatThreadBackgroundCss(presetId) : '';
    if (presetCss) {
      return {
        backgroundColor: 'transparent',
        backgroundImage: presetCss,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    }
    if (userCustomDataUrl) {
      const overlay =
        'linear-gradient(180deg, rgba(15, 23, 42, 0.38) 0%, rgba(15, 23, 42, 0.58) 100%)';
      return {
        backgroundColor: '#1a1f26',
        backgroundImage: `${overlay}, url(${JSON.stringify(userCustomDataUrl)})`,
        backgroundSize: 'cover, cover',
        backgroundPosition: 'center, center',
        backgroundRepeat: 'no-repeat',
      };
    }
    if (adminImagePath) {
      const url = resolveChatThreadImageUrl(adminImagePath);
      const overlay =
        'linear-gradient(180deg, rgba(15, 23, 42, 0.38) 0%, rgba(15, 23, 42, 0.58) 100%)';
      return {
        backgroundColor: '#1a1f26',
        backgroundImage: `${overlay}, url(${JSON.stringify(url)})`,
        backgroundSize: 'cover, cover',
        backgroundPosition: 'center, center',
        backgroundRepeat: 'no-repeat',
      };
    }
    return {
      backgroundColor: 'transparent',
      backgroundImage: CHAT_THREAD_DEFAULT_GRADIENT,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
    };
  }, [tick, adminImagePath, userCustomDataUrl]);
}

/** @deprecated используйте useChatThreadBackgroundStyle */
export const useChatThreadMessagesAreaStyle = useChatThreadBackgroundStyle;
